/**
 * Veriff SDK event handler for workflow legal ID flow.
 * Mirrors useVeriffEvents.ts from the IVA flow — same mount/unmount pattern.
 *
 * Only mounts Veriff when state === "capturing".
 * On FINISHED → calls onVerificationFinished (submit + poll + fetch).
 * On CANCELED → calls onVerificationError.
 * On RELOAD_REQUEST → onReload reloads the host page for camera-permission
 *   recovery (mainly Safari iOS); the frame is not closed.
 * Embedded mode is gated on device (mobile → Veriff modal); see isVeriffMobile().
 */

import { useEffect } from "react";
import { createVeriffFrame, MESSAGES } from "@veriff/incontext-sdk";
import { trackVeriffDeviceContext, trackVeriffCanceled, flushSentry } from "../../shared/utils/sentryTracking";
import { isVeriffMobile } from "../../shared/utils/veriffEnvironment";
import { logger } from "../../shared/utils/logger";

type WorkflowLegalIdState = "initializing" | "ready" | "capturing" | "processing" | "confirming" | "done" | "error";

interface UseWorkflowVeriffEventsParams {
  veriffSessionUrl: string | null;
  state: WorkflowLegalIdState;
  setState: (state: WorkflowLegalIdState) => void;
  onVerificationFinished: () => void;
  onVerificationError: (errorCode: string, message: string) => void;
}

export function useWorkflowVeriffEvents({
  veriffSessionUrl,
  state,
  setState,
  onVerificationFinished,
  onVerificationError,
}: UseWorkflowVeriffEventsParams) {
  useEffect(() => {
    // Only mount Veriff when user has clicked "Open camera" (state === "capturing")
    if (!veriffSessionUrl || state !== "capturing") return;

    logger.log("[WorkflowVeriff] Initializing Veriff SDK with session URL");

    // Device snapshot at capture start (covers users who later abandon).
    trackVeriffDeviceContext(null);

    // Veriff ignores embedded mode on mobile and renders its own modal; passing
    // embedded there would leave our #veriff-root container empty behind it.
    // Gate on Veriff's own mobile heuristic so both decisions stay consistent.
    const embedded = !isVeriffMobile();

    const veriffFrame = createVeriffFrame({
      url: veriffSessionUrl,
      embedded,
      ...(embedded && { embeddedOptions: { rootElementID: "veriff-root" } }),
      // Documented camera-permission recovery (mainly Safari iOS): reload the
      // host page so the browser re-prompts for camera access on a fresh
      // document load. A React re-render is NOT a host-page reload.
      onReload: async () => {
        logger.log("[WorkflowVeriff] Reload requested — reloading host page for camera-permission recovery");
        await flushSentry();
        window.location.reload();
      },
      onEvent: (msg) => {
        logger.log("[WorkflowVeriff] Event received:", msg);

        switch (msg) {
          case MESSAGES.STARTED:
            logger.log("[WorkflowVeriff] Started");
            break;

          case MESSAGES.SUBMITTED:
            // Do NOT change state here — wait for FINISHED.
            // Changing state would trigger cleanup (veriffFrame.close()) before
            // FINISHED fires, which prevents onVerificationFinished from being called.
            logger.log("[WorkflowVeriff] Submitted");
            break;

          case MESSAGES.FINISHED:
            // Veriff flow complete — transition to processing, then call handler
            logger.log("[WorkflowVeriff] Finished — triggering post-capture flow");
            setState("processing");
            onVerificationFinished();
            break;

          case MESSAGES.CANCELED:
            logger.log("[WorkflowVeriff] Canceled by user");
            trackVeriffCanceled(null);
            onVerificationError("veriff_failed", "Document capture was canceled");
            break;

          case MESSAGES.RELOAD_REQUEST:
            // Recovery handled by onReload (host-page reload). Do NOT close the
            // frame here — closing without reloading strands iOS Safari users.
            logger.log("[WorkflowVeriff] Reload request received (handled by onReload)");
            break;
        }
      },
    });

    logger.log("[WorkflowVeriff] SDK initialized successfully");

    return () => {
      logger.log("[WorkflowVeriff] Cleaning up Veriff SDK");
      veriffFrame.close();
    };
  }, [veriffSessionUrl, state, setState, onVerificationFinished, onVerificationError]);
}
